import clsx from 'clsx';
import { ChannelIcon } from '../../../../shared/ui/channel-icon/ChannelIcon';
import { IconButton } from '../../../../shared/ui/icon-button/IconButton';
import { OverflowPills } from '../../../../shared/ui/overflow-pills';
import { TruncatedText } from '../../../../shared/ui/truncated-text';
import pillStyles from '../../../../shared/ui/pill/Pill.module.css';
import scrollbarStyles from '../../../../shared/styles/scrollbarThin.module.css';
import selectStyles from '../../../../shared/ui/select/Select.module.css';
import { getMenuScrollLayout } from '../../../../shared/ui/select/lib/multiSelectMenuMetrics';
import {
  ArrowsUpDownIcon,
  EditIcon,
  EyeIcon,
  GripIcon,
  WarningTriangleIcon,
} from '../../../../shared/ui/icons';
import { useDropdownFlip } from '../../../../shared/hooks';
import { LanguageFlag } from '../../../templates/ui/language-flag';
import { CAMPAIGN_CADENCES_TYPE_TAB_KEY } from '../../constants/campaignCadencesTabs';
import styles from './CampaignCadenceCard.module.css';

const MOVE_TO_MAX_VISIBLE_STEPS = 6;

export function CampaignCadenceCardPanel({
  template,
  variant,
  isEditing = false,
  showLanguageFlag = false,
  appearance = 'default',
  isGripActive = false,
  stepNumber,
  totalSteps,
  panelRef,
  gripAttributes,
  gripListeners,
  moveToRef,
  moveToOpen = false,
  onMoveToToggle,
  onMoveToStep,
  onPreview,
  onEdit,
}) {
  const isTriggers = variant === CAMPAIGN_CADENCES_TYPE_TAB_KEY.TRIGGERS;
  const tags = template.tags ?? [];
  const tagNames = tags.map((tag) => String(tag?.name ?? ''));
  const warnings = template.warnings ?? [];
  // Trigger cards spell their warnings out in the banner below, so the panel carries no triangle.
  const showWarning = !isTriggers && warnings.length > 0;

  const { dropdownRef, openUpward } = useDropdownFlip(moveToOpen);

  const moveToSteps = Array.from({ length: totalSteps ?? 0 }, (_, i) => i + 1);
  const { needsMenuScroll: moveToNeedsScroll, menuMaxHeightPx: moveToMaxHeightPx } =
    getMenuScrollLayout(MOVE_TO_MAX_VISIBLE_STEPS, moveToSteps.length);

  const actionIcons =
    onPreview || (onEdit && !isEditing) ? (
      <div className={clsx(styles.iconGroup)}>
        {onPreview && (
          <IconButton
            ariaLabel="Preview template"
            className={clsx(styles.actionIcon)}
            size="md"
            onClick={onPreview}
          >
            <EyeIcon />
          </IconButton>
        )}
        {onEdit && !isEditing && (
          <IconButton
            ariaLabel="Edit template"
            className={clsx(styles.actionIcon)}
            size="md"
            onClick={onEdit}
          >
            <EditIcon />
          </IconButton>
        )}
      </div>
    ) : null;

  return (
    <div
      ref={panelRef}
      className={clsx(
        styles.templatePanel,
        isTriggers && styles.templatePanelTriggers,
        appearance === 'placeholder' && styles.templatePanelPlaceholder,
        appearance === 'dropTarget' && styles.templatePanelDropTarget,
        appearance === 'overlay' && styles.templatePanelOverlay,
      )}
    >
      {isEditing && (
        <IconButton
          ariaLabel="Drag to reorder"
          className={clsx(styles.grip, isGripActive && styles.gripActive)}
          {...gripAttributes}
          {...gripListeners}
        >
          <GripIcon />
        </IconButton>
      )}
      <div className={clsx(styles.templateContent)}>
        <div className={clsx(styles.templateInfo, isTriggers && styles.templateInfoFlat)}>
          <div className={clsx(styles.titleRow)}>
            <ChannelIcon className={clsx(styles.channelIcon)} channel={template.channel} />
            {showLanguageFlag && <LanguageFlag language={template.language} />}
            <TruncatedText
              as="p"
              className={styles.templateName}
              triggerClassName={styles.templateNameTrigger}
              tooltipContent={
                <span className={styles.templateNameTooltipText}>{template.name}</span>
              }
            >
              {template.name}
            </TruncatedText>
            {isTriggers && actionIcons && <div className={clsx(styles.actions)}>{actionIcons}</div>}
          </div>
          {(tags.length > 0 || showWarning) && (
            <div className={clsx(styles.detailsRow)}>
              <div className={clsx(styles.warningSlot)}>
                {showWarning && <WarningTriangleIcon className={clsx(styles.warningIndicator)} />}
              </div>
              {tags.length > 0 && (
                <OverflowPills
                  items={tagNames}
                  wrapClassName={clsx(styles.tagsPills)}
                  pillClassName={pillStyles.pill}
                  ariaLabel="Tags"
                  maxRows={1}
                  tooltipItemTransform={(value) => value.toUpperCase()}
                />
              )}
            </div>
          )}
        </div>
        {!isTriggers && (actionIcons || isEditing) && (
          <div className={clsx(styles.actions, moveToOpen && styles.actionsOpen)}>
            {actionIcons}
            {isEditing && (
              <div className={clsx(styles.moveToWrap)} ref={moveToRef}>
                <IconButton
                  ariaLabel="Move to another step"
                  className={clsx(styles.actionIcon)}
                  size="md"
                  onClick={onMoveToToggle}
                  aria-expanded={moveToOpen}
                >
                  <ArrowsUpDownIcon />
                </IconButton>
                {moveToOpen && (
                  <ul
                    ref={dropdownRef}
                    className={clsx(
                      selectStyles.menu,
                      styles.moveToDropdown,
                      openUpward && styles.moveToDropdownUp,
                      moveToNeedsScroll && scrollbarStyles.scrollbarThinY,
                      moveToNeedsScroll && scrollbarStyles.overscrollContain,
                    )}
                    style={
                      moveToNeedsScroll && moveToMaxHeightPx != null
                        ? { maxHeight: `${moveToMaxHeightPx}px` }
                        : undefined
                    }
                    role="listbox"
                  >
                    {moveToSteps.map((step) =>
                      step === stepNumber ? (
                        <li key={step} className={clsx(styles.moveToOptionItem)}>
                          <span className={clsx(selectStyles.option, styles.moveToOptionCurrent)}>
                            Step {step} (current)
                          </span>
                        </li>
                      ) : (
                        <li key={step} className={clsx(styles.moveToOptionItem)}>
                          <button
                            type="button"
                            className={clsx(selectStyles.option, styles.moveToOption)}
                            onClick={() => onMoveToStep?.(step)}
                          >
                            Step {step}
                          </button>
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
